'use client';

import { useMemo } from 'react';

import { useGetChatDestinationsQuery } from '@/features/chat/api/chatApi';
import { CloseIcon, Drawer } from '@web/ui';

import styles from './ChatContextDrawer.module.scss';
import { ChatPanel } from './ChatPanel';

import type { ActiveChat } from '@/app/(protected)/[workspace_slug]/chat/ChatContext';
import type { ChatConversation } from '@/features/chat/types';

export type ChatContextTarget = {
  type: 'job' | 'deliverable' | 'task';
  id: string;
  label: string;
  sourceJobId?: string | null;
  sourceJobLabel?: string | null;
};

function getJobConversation(
  destinations: ChatConversation[],
  sourceJobId?: string | null,
) {
  if (!sourceJobId) {
    return null;
  }

  return destinations.find(
    (destination) =>
      destination.conversation_type === 'job_channel' &&
      destination.job?.id === sourceJobId,
  );
}

function getTargetEyebrow(target: ChatContextTarget) {
  if (target.type === 'task') {
    return 'Task Chat';
  }

  if (target.type === 'deliverable') {
    return 'Deliverable Chat';
  }

  return 'Job Chat';
}

function getTargetDescription(target: ChatContextTarget) {
  if (target.sourceJobLabel) {
    return `Messages tagged to this ${target.type} in ${target.sourceJobLabel}.`;
  }

  return `Messages tagged to this ${target.type}.`;
}

export function ChatContextDrawer({
  open,
  target,
  onClose,
  onExited,
}: {
  open: boolean;
  target: ChatContextTarget;
  onClose: () => void;
  onExited?: () => void;
}) {
  const { data } = useGetChatDestinationsQuery(
    { conversation_type: 'job_channel', page_size: 100 },
    { skip: !open || !target.sourceJobId },
  );
  const destinations = data?.data?.results ?? [];
  const jobConversation = getJobConversation(destinations, target.sourceJobId);
  const activeChat = useMemo<ActiveChat>(
    () => ({
      id: jobConversation?.id ?? target.sourceJobId ?? target.id,
      type: 'job_channel',
      title: target.label,
      description: target.sourceJobLabel ?? undefined,
      jobId: target.sourceJobId ?? undefined,
      isArchived: jobConversation?.is_archived,
      isReadOnly: jobConversation?.is_read_only || !target.sourceJobId,
    }),
    [jobConversation, target],
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      onExited={onExited}
      width="min(520px, 92vw)"
      paperClassName={styles.chatContextDrawer}
    >
      <header className={styles.chatContextDrawer__header}>
        <div>
          <span className={styles.chatContextDrawer__eyebrow}>
            {getTargetEyebrow(target)}
          </span>
        </div>
        <button
          type="button"
          className={styles.chatContextDrawer__closeButton}
          aria-label="Close chat"
          onClick={onClose}
        >
          <CloseIcon size={16} />
        </button>
      </header>
      <div className={styles.chatContextDrawer__body}>
        <div className={styles.chatContextDrawer__panel}>
          <ChatPanel
            search=""
            activeChat={activeChat}
            filters={{
              workContextType: target.type,
              workContextId: target.id,
              workContextLabel: target.label,
            }}
            contextTag={{
              target_type: target.type,
              target_id: target.id,
            }}
          />
        </div>
      </div>
    </Drawer>
  );
}
